import { useState, useEffect } from 'react';
import { collection, query, getDocs, updateDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { UserCheck, UserX, Clock, Mail, Store } from 'lucide-react';
import { toast } from 'sonner';

interface VendorRequest {
  id: string;
  userId: string;
  email: string;
  storeName: string;
  storeDescription: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
}

export function VendorRequestsManager() {
  const [requests, setRequests] = useState<VendorRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'vendor_requests'));
      const snapshot = await getDocs(q);
      const fetched = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as VendorRequest[];
      
      // Sort: pending first, then by date desc
      fetched.sort((a, b) => {
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (a.status !== 'pending' && b.status === 'pending') return 1;
        return b.createdAt?.toMillis() - a.createdAt?.toMillis();
      });
      
      setRequests(fetched);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch vendor requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAction = async (request: VendorRequest, status: 'approved' | 'rejected') => {
    try {
      // Update request status
      await updateDoc(doc(db, 'vendor_requests', request.id), { status });
      
      if (status === 'approved') {
        // Promote user to vendor
        await updateDoc(doc(db, 'users', request.userId), { 
          role: 'vendor',
          storeName: request.storeName,
          storeDescription: request.storeDescription
        });
        
        // Initialize store document
        await setDoc(doc(db, 'stores', request.userId), {
          storeName: request.storeName,
          storeDescription: request.storeDescription,
          bannerType: 'color',
          bannerColors: ['#f3f4f6', '#f3f4f6', '#f3f4f6'],
          themeType: 'solid',
          themeColors: ['#171717', '#171717', '#171717'],
          updatedAt: serverTimestamp()
        }, { merge: true });

        toast.success(`User ${request.email} promoted to vendor.`);
      } else {
        toast.info(`Request from ${request.email} rejected.`);
      }
      
      fetchRequests();
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${status} request.`);
    }
  };

  if (loading) return <div className="p-8 text-center text-neutral-500">Loading requests...</div>;

  if (requests.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-12 text-center">
        <Store className="w-12 h-12 text-neutral-300 dark:text-neutral-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white">No Vendor Requests</h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 font-light">
          When users apply to become vendors, their requests will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
      <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
        <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
          <Store className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
          Vendor Applications
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 font-light">
          Review and approve or reject vendor access requests.
        </p>
      </div>
      
      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {requests.map(req => (
          <div key={req.id} className="p-6">
            <div className="flex flex-col md:flex-row justify-between gap-6">
              <div className="space-y-4 flex-1">
                <div className="flex items-center gap-3">
                  <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                    req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {req.status}
                  </div>
                  <span className="text-xs text-neutral-400">
                    {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleString() : 'Unknown date'}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-neutral-400" />
                    <span className="text-neutral-900 dark:text-white font-medium">{req.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Store className="w-4 h-4 text-neutral-400" />
                    <span className="text-neutral-900 dark:text-white font-medium">{req.storeName}</span>
                  </div>
                </div>
                
                <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-widest mb-1">Store Description</p>
                  <p className="text-sm text-neutral-700 dark:text-neutral-300 italic">"{req.storeDescription}"</p>
                </div>
              </div>
              
              {req.status === 'pending' && (
                <div className="flex md:flex-col gap-2 shrink-0">
                  <button
                    onClick={() => handleAction(req, 'approved')}
                    className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors"
                  >
                    <UserCheck className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleAction(req, 'rejected')}
                    className="flex-1 flex items-center justify-center gap-2 border border-red-200 text-red-600 px-4 py-2 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors"
                  >
                    <UserX className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
